import cs from "classnames";
import React from "react";

import { useTrackFocusState } from "src/common/hooks/useTrackFocusState";

import "./Checkbox.css";

const noop = (): void => {};

interface CheckboxProps {
	disabled?: boolean;
	checked?: boolean;
	onChangeChecked?(checked: boolean, event: { shiftKey: boolean }): unknown;
}

export const Checkbox: React.FunctionComponent<
	React.PropsWithChildren<CheckboxProps>
> = ({ disabled, checked, onChangeChecked = noop, children }) => {
	const [isFocused, onFocus, onBlur] = useTrackFocusState();

	const onClick = React.useCallback(
		(event: React.MouseEvent) => {
			onBlur();
			onChangeChecked(!checked, { shiftKey: event.shiftKey });
		},
		[onBlur, onChangeChecked, checked]
	);

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === " ") {
				event.preventDefault();
				onChangeChecked(!checked, { shiftKey: event.shiftKey });
				onFocus();
			}
		},
		[onFocus, onChangeChecked, checked]
	);

	return (
		<label
			className={cs("checkbox", {
				"checkbox--disabled": disabled,
				"checkbox--checked": checked,
				"checkbox--focused": isFocused
			})}
			onClick={onClick}
		>
			<div
				role="checkbox"
				aria-checked={checked}
				className="checkbox__input"
				tabIndex={disabled ? -1 : 0}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
			>
				<div className="checkbox__ripple" />
				<div className="checkbox__box" />
			</div>
			{children && <div className="checkbox__label">{children}</div>}
		</label>
	);
};
